import React, { useEffect, useRef } from "react";
import { Box, Paper } from "@mui/material";

const BouncingSpeakers = ({
  speaker1,
  speaker2,
  availableSpeakers,
  theme,
  styles,
}) => {
  const speaker1Data = availableSpeakers?.find((s) => s.id === speaker1);
  const speaker2Data = availableSpeakers?.find((s) => s.id === speaker2);
  const avatar1Src =
    speaker1Data?.avatar ||
    "https://sportkuriken.se/wp-content/uploads/ash.png";
  const avatar2Src =
    speaker2Data?.avatar ||
    "https://sportkuriken.se/wp-content/uploads/nova.png";

  const containerRef = useRef(null);
  const avatar1Ref = useRef(null);
  const avatar2Ref = useRef(null);

  // Use refs instead of state for animation values
  const animationRef = useRef(null);
  const positionsRef = useRef({
    pos1: { x: 100, y: 100 },
    pos2: { x: 200, y: 150 },
    dir1: { x: 1, y: 1 },
    dir2: { x: -1, y: 1 },
    rotation1: 0,
    rotation2: 0,
  });

  useEffect(() => {
    // Cache container dimensions to avoid layout thrashing
    let containerWidth = 0;
    let containerHeight = 0;
    const speed1 = { x: 0.2, y: 0.2 }; // Slowed down for gentler movement
    const speed2 = { x: 0.2, y: 0.2 }; // Slightly different speeds
    const avatarSize = 124;

    // Measure the container once
    const updateContainerSize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      containerWidth = rect.width;
      containerHeight = rect.height;
    };

    // Create resize observer to update container size when needed
    const resizeObserver = new ResizeObserver(() => {
      updateContainerSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      updateContainerSize();
    }

    const animate = () => {
      const values = positionsRef.current;
      const maxX = containerWidth - avatarSize;
      const maxY = containerHeight - avatarSize;

      // Update position and direction for avatar 1
      let newX1 = values.pos1.x + speed1.x * values.dir1.x;
      let newY1 = values.pos1.y + speed1.y * values.dir1.y;

      if (newX1 <= 0 || newX1 >= maxX) {
        values.dir1.x = -values.dir1.x;
        newX1 = newX1 <= 0 ? 0 : maxX;
      }
      if (newY1 <= 0 || newY1 >= maxY) {
        values.dir1.y = -values.dir1.y;
        newY1 = newY1 <= 0 ? 0 : maxY;
      }

      values.pos1.x = newX1;
      values.pos1.y = newY1;

      // Update position and direction for avatar 2
      let newX2 = values.pos2.x + speed2.x * values.dir2.x;
      let newY2 = values.pos2.y + speed2.y * values.dir2.y;

      if (newX2 <= 0 || newX2 >= maxX) {
        values.dir2.x = -values.dir2.x;
        newX2 = newX2 <= 0 ? 0 : maxX;
      }
      if (newY2 <= 0 || newY2 >= maxY) {
        values.dir2.y = -values.dir2.y;
        newY2 = newY2 <= 0 ? 0 : maxY;
      }

      values.pos2.x = newX2;
      values.pos2.y = newY2;

      // Update rotations
      values.rotation1 = (values.rotation1 + 0.2) % 360;
      values.rotation2 = (values.rotation2 - 0.2 + 360) % 360;

      // Apply transforms directly to DOM for better performance
      if (avatar1Ref.current) {
        avatar1Ref.current.style.transform = `translate(${values.pos1.x}px, ${values.pos1.y}px)`;
        avatar1Ref.current.querySelector("img").style.transform =
          `rotate(${values.rotation1}deg)`;
      }

      if (avatar2Ref.current) {
        avatar2Ref.current.style.transform = `translate(${values.pos2.x}px, ${values.pos2.y}px)`;
        avatar2Ref.current.querySelector("img").style.transform =
          `rotate(${values.rotation2}deg)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
    };
  }, []); // Empty dependency array to run only once

  return (
    <Box
      elevation={1}
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: 0,
        borderRadius: 2,
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          height: "230px",
          position: "relative",
          boxShadow: 0,
          borderRadius: "8px",
          overflow: "hidden",
          transition: "all 0.2s",
          "&:hover": {
            transform: "scale(1.02)",
          },
        }}
      >
        <Box
          ref={avatar1Ref}
          sx={{
            position: "absolute",
            width: "130px",
            height: "130px",
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            willChange: "transform", // Hint for browser optimization
            ...styles.softBoxShadow,
          }}
        >
          <Box
            component="img"
            src={avatar1Src}
            sx={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              willChange: "transform", // Hint for browser optimization
            }}
          />
        </Box>
        <Box
          ref={avatar2Ref}
          sx={{
            position: "absolute",
            width: "130px",
            height: "130px",
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            willChange: "transform", // Hint for browser optimization
            ...styles.softBoxShadow,
          }}
        >
          <Box
            component="img"
            src={avatar2Src}
            sx={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              willChange: "transform", // Hint for browser optimization
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BouncingSpeakers;
